import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page } from 'react-pdf'
import { Loader2, AlertCircle } from 'lucide-react'
import PDFControls from './PDFControls'
import PDFThumbnailSidebar from './PDFThumbnailSidebar'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

interface PDFViewerProps {
  fileUrl: string
  fileName?: string
}

export default function PDFViewer({ fileUrl, fileName }: PDFViewerProps) {
  const { t } = useTranslation()
  const [numPages, setNumPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [zoom, setZoom] = useState<number>(100)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({})
  const isScrollingProgrammatically = useRef(false)
  const scrollDetectionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set())

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setCurrentPage(1)
    setError(null)
    setRenderedPages(new Set())
  }

  const handlePageRenderSuccess = (pageNumber: number) => {
    setRenderedPages(prev => new Set(prev).add(pageNumber))
  }

  const handleDocumentLoadError = () => {
    setError(t('pdfViewer.errors.loadFailed'))
  }

  // Scroll to specific page
  const scrollToPage = (page: number) => {
    const pageElement = pageRefs.current[page]
    if (pageElement) {
      isScrollingProgrammatically.current = true
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setCurrentPage(page)

      // Re-enable scroll detection after animation completes (~600ms for smooth scroll)
      setTimeout(() => {
        isScrollingProgrammatically.current = false
      }, 600)
    }
  }

  // Page navigation
  const goToFirstPage = () => scrollToPage(1)
  const goToPreviousPage = () => scrollToPage(Math.max(1, currentPage - 1))
  const goToNextPage = () => scrollToPage(Math.min(numPages, currentPage + 1))
  const goToLastPage = () => scrollToPage(numPages)
  const goToPage = (page: number) => scrollToPage(page)

  // Detect current page based on scroll position
  useEffect(() => {
    const container = containerRef.current
    if (!container || numPages === 0) return

    const detectCurrentPage = () => {
      const containerHeight = container.clientHeight

      // Find which page is most visible
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const pageElement = pageRefs.current[pageNum]
        if (pageElement) {
          const rect = pageElement.getBoundingClientRect()
          const containerRect = container.getBoundingClientRect()

          // Check if page is in viewport
          const pageTop = rect.top - containerRect.top
          const pageBottom = rect.bottom - containerRect.top

          // If page center is in viewport, it's the current page
          const pageCenter = (pageTop + pageBottom) / 2
          if (pageCenter >= 0 && pageCenter <= containerHeight) {
            setCurrentPage(pageNum)
            break
          }
        }
      }
    }

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) return

      if (scrollDetectionTimeout.current) {
        clearTimeout(scrollDetectionTimeout.current)
      }

      scrollDetectionTimeout.current = setTimeout(() => {
        detectCurrentPage()
      }, 30)
    }

    container.addEventListener('scroll', handleScroll)
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (scrollDetectionTimeout.current) {
        clearTimeout(scrollDetectionTimeout.current)
      }
    }
  }, [numPages])

  // Zoom controls
  const zoomIn = () => {
    setZoom((prev) => Math.min(200, prev + 25))
    setRenderedPages(new Set())
  }
  const zoomOut = () => {
    setZoom((prev) => Math.max(25, prev - 25))
    setRenderedPages(new Set())
  }
  const resetZoom = () => {
    setZoom(100)
    setRenderedPages(new Set())
  }

  // Calculate page width based on zoom
  const pageWidth = (800 * zoom) / 100
  const pageHeight = Math.round(pageWidth * 1.414)

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-base-100">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-error mx-auto mb-4" />
          <p className="text-lg font-medium text-base-content">{error}</p>
          <p className="text-sm text-base-content/70 mt-2">{fileName}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-base-100">
      {/* Controls */}
      {numPages > 0 && (
        <PDFControls
          currentPage={currentPage}
          totalPages={numPages}
          zoom={zoom}
          onFirstPage={goToFirstPage}
          onPreviousPage={goToPreviousPage}
          onNextPage={goToNextPage}
          onLastPage={goToLastPage}
          onGoToPage={goToPage}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
        />
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Thumbnail Sidebar */}
        {numPages > 0 && (
          <PDFThumbnailSidebar
            file={fileUrl}
            currentPage={currentPage}
            totalPages={numPages}
            onPageSelect={goToPage}
          />
        )}

        {/* PDF Document Viewer */}
        <div ref={containerRef} className="flex-1 overflow-auto bg-base-200">
          <div className="flex flex-col items-center p-8 gap-4">
            <Document
              file={fileUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading={
                <div className="flex items-center justify-center h-screen">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-sm text-base-content/70">
                      {t('pdfViewer.loading')}
                    </p>
                  </div>
                </div>
              }
            >
              {Array.from({ length: numPages }, (_, index) => {
                  const pageNum = index + 1
                  return (
                    <div
                      key={pageNum}
                      ref={(el) => {
                        pageRefs.current[pageNum] = el
                      }}
                      className="mb-4"
                    >
                      <div className="bg-white shadow-lg relative">
                        <Page
                          pageNumber={pageNum}
                          width={pageWidth}
                          onRenderSuccess={() => handlePageRenderSuccess(pageNum)}
                          loading={
                            <div
                              className="flex items-center justify-center bg-white"
                              style={{ width: pageWidth, height: pageHeight }}
                            >
                              <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                          }
                        />
                        {!renderedPages.has(pageNum) && (
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-white"
                          >
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
            </Document>
          </div>
        </div>
      </div>
    </div>
  )
}
